/**
 * SlabReality API Service
 * =======================
 * 100% SUPABASE ARCHITECTURE - NO MONGODB
 * 
 * Returns:
 * - materials
 * - facets (for dynamic filters)
 * - related materials
 */

import { supabase, isSupabaseConfigured } from '../lib/supabase';

/**
 * Call the main SlabReality API
 * Returns materials with DNA and facets for filtering
 * SUPABASE ONLY - No MongoDB fallback
 */
export const getSlabRealityAPI = async (params = {}) => {
  console.log('🔷 getSlabRealityAPI() - Fetching materials from Supabase');
  console.log('📍 Params:', params);
  
  if (!isSupabaseConfigured || !supabase) {
    console.error('❌ Supabase not configured');
    return { success: false, data: null, error: 'Supabase not configured' };
  }
  
  // Primary: Try Supabase RPC
  try {
    const { data, error } = await supabase.rpc('get_slabreality_api', params);
    
    if (!error && data) {
      console.log('✅ Supabase RPC: Success');
      return { success: true, data };
    }
    
    // Fallback: Direct Supabase query
    return await fallbackSupabaseQuery(params);
  } catch (err) {
    console.error('❌ Supabase RPC error:', err);
    // Try direct query as fallback
    return await fallbackSupabaseQuery(params);
  }
};

/**
 * Fallback: Direct Supabase query (if RPC doesn't exist)
 * Fixed: removed material_dna relation (doesn't exist), corrected column names
 */
const fallbackSupabaseQuery = async (params = {}) => {
  console.log('📦 Fallback: Direct Supabase materials query');
  
  try {
    // Simple query without joins - materials table has all needed fields
    const { data: materials, error } = await supabase
      .from('materials')
      .select('*');
    
    if (error) {
      // Extract error message safely
      const errorMsg = error?.message || error?.toString() || 'Unknown error';
      console.warn('⚠️ Supabase query failed:', errorMsg);
      return { success: false, data: null, error: errorMsg };
    }
    
    console.log('✅ Supabase query success, materials:', materials?.length || 0);
    return buildResponse(materials || []);
    
  } catch (err) {
    console.error('❌ Supabase query error:', err);
    return { success: false, data: null, error: err?.message || 'Query failed' };
  }
};

/**
 * Build standardized API response with facets
 * Updated for Supabase schema: uses 'category' instead of 'stone_type', 'id' instead of 'material_id'
 * 
 * IMMAGINI: SOLO image_main (R2) - NO FALLBACK CDN
 */
const buildResponse = (materials) => {
  // Normalize material data from Supabase format
  const processed = materials.map(m => {
    return {
      // Core fields - use 'id' from Supabase, map to 'material_id' for compatibility
      id: m.id,
      material_id: m.id,
      name: m.name,
      slug: m.slug || m.id,
      description: m.description,
      // Category/Type - Supabase uses 'category', normalize to 'stone_type' for UI
      category: m.category,
      stone_type: m.category, // Map category -> stone_type for UI compatibility
      // IMMAGINI: PRIORITÀ image_url (R2), poi image_main
      image_main: m.image_url || m.image_main,
      image_url: m.image_url || m.image_main,
      // Properties
      finishes: m.finishes || [],
      applications: m.applications || [],
      color: m.color_name || m.dominant_color,
      origin: m.origin,
      producer: m.producer,
      // DNA fields from materials table
      color_family: m.dominant_color,
      dominant_color: m.dominant_color,
      pattern_type: m.pattern,
      pattern: m.pattern,
      // Flags
      is_featured: m.is_featured,
      is_iconic: m.is_iconic
    };
  });
  
  // Build facets from data
  const facets = {
    category: extractFacet(processed, 'category'),
    color_family: extractFacet(processed, 'color_family'),
    pattern_type: extractFacet(processed, 'pattern_type'),
    tone: extractFacet(processed, 'tone'),
    application_role: extractFacet(processed, 'application_role')
  };
  
  console.log('✅ Processed materials:', processed.length);
  console.log('📊 Facets:', Object.keys(facets).map(k => `${k}: ${facets[k].length}`));
  
  return {
    success: true,
    data: {
      materials: processed,
      facets,
      total: processed.length
    }
  };
};

/**
 * Extract unique facet values from materials
 */
const extractFacet = (materials, field) => {
  const values = materials
    .map(m => m[field])
    .filter(v => v != null && v !== '')
    .reduce((acc, val) => {
      // Handle arrays or single values
      const items = Array.isArray(val) ? val : [val];
      items.forEach(item => {
        if (item && !acc.includes(item)) {
          acc.push(item);
        }
      });
      return acc;
    }, [])
    .sort();
  
  return values;
};

/**
 * Get related materials based on DNA similarity
 */
export const getRelatedMaterials = async (materialId, limit = 4) => {
  console.log('🔷 getRelatedMaterials() for:', materialId);
  
  // For now, use client-side similarity (already implemented)
  // This can be upgraded to server-side RPC later
  
  const { success, data } = await getSlabRealityAPI();
  
  if (!success || !data?.materials) {
    return { success: false, data: [] };
  }
  
  const sourceMaterial = data.materials.find(m => m.id === materialId);
  if (!sourceMaterial) {
    return { success: false, data: [] };
  }
  
  // Import similarity function
  const { getSimilarMaterials } = await import('../utils/materialSimilarity');
  
  const similar = getSimilarMaterials(sourceMaterial, data.materials, limit);
  
  return {
    success: true,
    data: similar.map(s => s.material)
  };
};

export default {
  getSlabRealityAPI,
  getRelatedMaterials
};
